/* eslint-disable @typescript-eslint/no-invalid-void-type */
import { Options } from '../options.interface';
import { SUGGESTIONS_API_CONFIG } from './suggestions-api.config';
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  CrousWish,
  CrousWishResource,
  LocaleEnum,
  Organization,
  Question,
  Suggestion,
  SuggestionsListing,
  SuggestionsListingV2,
  SuggestionsListingV3,
} from '@wizbii/utils/models';
import { Observable } from 'rxjs';

export type UserType = 'member' | 'visitor';

export interface QuestionWithResponse {
  question: Question;
  response: string;
  responseAsString: string;
}

export interface SuggestionResponse {
  question: Question;
  annualReward: number;
  currency: string;
  nbEligibleSuggestions: number;
  nbPossibleSuggestions: number;
  nbIncompatibleSuggestions: number;
  nbDeniedSuggestions: number;
  canGoBack: boolean;
}

export enum CollectivityEnum {
  Pau = 'pau',
  Garges = 'garges',
}

@Injectable({
  providedIn: 'root',
})
export class SuggestionsWebservice {
  readonly #httpClient = inject(HttpClient);
  readonly #suggestionsApiConfig = inject(SUGGESTIONS_API_CONFIG);

  readonly #baseUrl = `${this.#suggestionsApiConfig.baseUrl}/external`;

  getFirstQuestion(
    suggestionSetId: string,
    userId: string,
    userType: UserType = 'visitor',
    options?: Options
  ): Observable<SuggestionResponse> {
    return this.#httpClient.post<SuggestionResponse>(
      `${this.#baseUrl}/questions/${suggestionSetId}/${userType}/${userId}/first`,
      {},
      options
    );
  }

  getQuestion(questionId: string, locale: LocaleEnum = LocaleEnum.fr_FR, options?: Options): Observable<Question> {
    return this.#httpClient.get<Question>(`${this.#baseUrl}/questions/${locale}/${questionId}`, options);
  }

  getQuestions(questionIds: string[], locale = LocaleEnum.fr_FR, options?: Options): Observable<Question[]> {
    return this.#httpClient.get<Question[]>(`${this.#baseUrl}/collection-question/${locale}/${questionIds}`, options);
  }

  answerQuestion(
    questionId: string,
    value: any,
    suggestionSetId: string,
    userId: string,
    userType: UserType = 'visitor',
    options?: Options
  ): Observable<SuggestionResponse> {
    return this.#httpClient.post<SuggestionResponse>(
      `${this.#baseUrl}/questions/${suggestionSetId}/${userType}/${userId}/next`,
      {
        questionId,
        value,
      },
      options
    );
  }

  revertDatabag(
    suggestionSetId: string,
    userId: string,
    userType: UserType = 'visitor',
    options?: Options
  ): Observable<SuggestionResponse> {
    return this.#httpClient.post<SuggestionResponse>(
      `${this.#baseUrl}/questions/${suggestionSetId}/${userType}/${userId}/revert`,
      null,
      options
    );
  }

  getSuggestions(
    suggestionSetId: string,
    userId: string,
    userType: UserType = 'visitor',
    options?: Options
  ): Observable<SuggestionsListing> {
    return this.#httpClient.get<SuggestionsListing>(
      `${this.#baseUrl}/suggestions/${suggestionSetId}/${userType}/${userId}/suggestions`,
      options
    );
  }

  getSuggestionsV2(
    suggestionSetId: string,
    userId: string,
    userType: UserType = 'visitor',
    options?: Options
  ): Observable<SuggestionsListingV2> {
    return this.#httpClient.get<SuggestionsListingV2>(
      `${this.#baseUrl}/v2/suggestions/${suggestionSetId}/${userType}/${userId}/suggestions`,
      options
    );
  }

  getSuggestionsV3(
    suggestionSetId: string,
    userId: string,
    userType: UserType = 'visitor',
    options?: Options
  ): Observable<SuggestionsListingV3> {
    return this.#httpClient.get<SuggestionsListingV3>(
      `${this.#baseUrl}/v3/suggestions/${suggestionSetId}/${userType}/${userId}/suggestions`,
      options
    );
  }

  getSuggestion(
    suggestionId: string,
    userId: string,
    userType: UserType = 'member',
    options?: Options
  ): Observable<Suggestion> {
    return this.#httpClient.get<Suggestion>(
      `${this.#baseUrl}/suggestions/${userType}/${userId}/${suggestionId}`,
      options
    );
  }

  /**
   * @param suggestionIds Array of suggestion ids
   * @param options HttpClient options
   */
  getSuggestionByIds(suggestionIds: string[], options?: Options): Observable<Suggestion[]> {
    const params = {
      ...(options?.params ?? {}),
      suggestionIds: suggestionIds.join(','),
    };

    return this.#httpClient.get<Suggestion[]>(`${this.#baseUrl}/suggestions`, { ...options, params });
  }

  getSuggestionsCount(suggestionSetId: string, options?: Options): Observable<number> {
    return this.#httpClient.get<number>(`${this.#baseUrl}/suggestions/${suggestionSetId}/count`, options);
  }

  getQuestionsWithResponses(
    suggestionSetId: string,
    userId: string,
    userType: UserType = 'visitor',
    options?: Options
  ): Observable<{
    questionWithResponses: QuestionWithResponse[];
  }> {
    return this.#httpClient.get<{
      questionWithResponses: QuestionWithResponse[];
    }>(`${this.#baseUrl}/questions/${suggestionSetId}/${userType}/${userId}`, options);
  }

  getQuestionWithResponse(
    suggestionSetId: string,
    userId: string,
    questionId: string,
    userType: UserType = 'visitor',
    options?: Options
  ): Observable<QuestionWithResponse> {
    return this.#httpClient.get<QuestionWithResponse>(
      `${this.#baseUrl}/questions/${suggestionSetId}/${userType}/${userId}/${questionId}`,
      options
    );
  }

  clearUserData(userId: string, userType: UserType = 'visitor', options?: Options): Observable<void> {
    return this.#httpClient.patch<void>(`${this.#baseUrl}/userData/${userType}/${userId}`, null, options);
  }

  downloadUserPdf(userId: string, userType: string, suggestionSetId: string): Observable<any> {
    return this.#httpClient.get(`${this.#baseUrl}/anwsers/${suggestionSetId}/${userType}/${userId}/pdf`, {
      responseType: 'blob',
    });
  }

  downloadUserAdditionalPdf(
    userId: string,
    userType: string,
    suggestionId: string,
    locale = LocaleEnum.fr_FR
  ): Observable<any> {
    return this.#httpClient.get(`${this.#baseUrl}/anwsers/${locale}/${userType}/${userId}/${suggestionId}/pdf`, {
      responseType: 'blob',
    });
  }

  getUserAdditionalDocuments(
    userId: string,
    userType: string,
    suggestionId: string,
    locale = LocaleEnum.fr_FR
  ): Observable<any> {
    return this.#httpClient.get(
      `${this.#baseUrl}/additional-questions/answers/documents/${locale}/${userType}/${userId}/${suggestionId}`
    );
  }

  getUserAdditionalSensibleQuestionById(
    userId: string,
    userType: string,
    suggestionId: string,
    locale = LocaleEnum.fr_FR
  ): Observable<any> {
    return this.#httpClient.get(
      `${this.#baseUrl}/additional-questions/answers/sensible/${locale}/${userType}/${userId}/${suggestionId}`
    );
  }

  getUserAdditionalBankerQuestionById(
    userId: string,
    userType: string,
    suggestionId: string,
    locale = LocaleEnum.fr_FR
  ): Observable<any> {
    return this.#httpClient.get(
      `${this.#baseUrl}/additional-questions/answers/banker/${locale}/${userType}/${userId}/${suggestionId}`
    );
  }

  getIneligibleSuggestions(
    userId: string,
    userType: UserType = 'member',
    locale: LocaleEnum = LocaleEnum.fr_FR
  ): Observable<string[]> {
    return this.#httpClient.get<string[]>(`${this.#baseUrl}/helps/${locale}/${userType}/${userId}`);
  }

  getOrganizations(): Observable<Organization[]> {
    return this.#httpClient.get<Organization[]>(`${this.#baseUrl}/organizations`);
  }

  getEligibilityFormResponses(
    suggestionSetId: string,
    userId: string,
    userType: UserType = 'member'
  ): Observable<{ questionWithResponses: QuestionWithResponse[] }> {
    return this.#httpClient.get<{ questionWithResponses: QuestionWithResponse[] }>(
      `${this.#baseUrl}/answers/${suggestionSetId}/${userType}/${userId}`
    );
  }

  getComplementaryFormResponses(
    suggestionId: string,
    userId: string,
    userType: UserType = 'member',
    locale: LocaleEnum = LocaleEnum.fr_FR
  ): Observable<{ questionWithResponses: QuestionWithResponse[] }> {
    return this.#httpClient.get<{ questionWithResponses: QuestionWithResponse[] }>(
      `${this.#baseUrl}/additional/answers/${locale}/${userType}/${userId}/${suggestionId}`
    );
  }

  getUserComplementaryFormResponse(
    questionId: string,
    userId: string,
    userType: UserType = 'member'
  ): Observable<{ value: string }> {
    return this.#httpClient.get<{ value: string }>(
      `${this.#baseUrl}/user-data-additional/${userType}/${userId}/${questionId}`
    );
  }

  postUserComplementaryFormResponse(
    questionId: string,
    responseValue: any,
    userId: string,
    userType: UserType = 'member'
  ): Observable<void> {
    return this.#httpClient.post<void>(`${this.#baseUrl}/user-data-additional/question/${userType}/${userId}`, {
      questionId,
      value: responseValue,
    });
  }

  getQuestionsUpdatedBySuggestionId(userId: string, suggestionId: string): Observable<string[]> {
    return this.#httpClient.get<string[]>(`${this.#baseUrl}/dossiers/updated/${userId}/${suggestionId}`);
  }

  getCrousWishNextQuestionValues(
    crousWishValue: { [key: string]: string },
    options?: Options
  ): Observable<CrousWish | { id: string }> {
    return this.#httpClient.get<CrousWish | { id: string }>(`${this.#baseUrl}/crous/wish-list`, {
      ...options,
      params: crousWishValue,
    });
  }

  getCrousWish(crousWishId: string, options?: Options): Observable<CrousWishResource> {
    return this.#httpClient.get<CrousWishResource>(`${this.#baseUrl}/crous/wish/${crousWishId}`, options);
  }

  getUserResponsesByQuestionIds(userId: string, questionIds: string[], locale = LocaleEnum.fr_FR): Observable<any> {
    return this.#httpClient.post(`${this.#baseUrl}/additional/answers/${locale}/member/${userId}`, [...questionIds]);
  }

  getUserComplementaryFormState(
    suggestionId: string,
    userId: string,
    questionId?: string,
    userType: UserType = 'member'
  ): Observable<{ next?: string; previous?: string }> {
    return this.#httpClient.post<{ next: string }>(
      `${this.#baseUrl}/additional-questions/state/${userType}/${userId}/${suggestionId}`,
      {
        ...(!!questionId && { currentQuestionId: questionId }),
      }
    );
  }

  checkUserIsInCollectivity(userId: string, userType: UserType = 'member'): Observable<CollectivityEnum | null> {
    return this.#httpClient.get<CollectivityEnum | null>(
      `${this.#baseUrl}/userData/${userType}/${userId}/user-collectivity`
    );
  }

  createMemberFromVisitor(userId: string, body: any = {}, options?: Options): Observable<void> {
    return this.#httpClient.post<void>(`${this.#baseUrl}/userData/create-member-from-visitor/${userId}`, body, options);
  }
}
